import dayjs from 'dayjs';
import React, { useCallback, useRef, useState } from 'react';

import { L10nDate } from '@travel/i18n';
import { ArrowRightLine, Calendar } from '@travel/icons/ui';
import { useDeviceType, MOBILE_SCREEN } from '@travel/traveler-core/hooks';
import useDialogHandler from '@travel/traveler-core/hooks/useDialogHandler';
import { MONTH_DAY_YOUBI_WITH_SPACE_FORMAT } from '@travel/traveler-core/utils/dateTime';
import { cx } from '@travel/utils/src/classNames';

import HiddenInput from 'favoriteList/components/SearchForm/components/HiddenInput';
import IconTextButton from 'favoriteList/components/SearchForm/components/IconTextButton';
import labels from 'favoriteList/components/SearchForm/constants/labels';
import { URL_KEYS } from 'favoriteList/components/SearchForm/utils/search';
import DatePicker from '../DatePicker';
import DateButton from './DateButton';

import styles from './dateRow.module.scss';

type DateButtonType = 'startDate' | 'endDate';

type Props = {
  className?: string;
  /** Node of start date title */
  startDateTitle?: React.ReactNode;
  /** Node of end date title */
  endDateTitle?: React.ReactNode;
  /** String of selected start date */
  startDate: string;
  /** String of selected end date */
  endDate: string;
  /** Function to be called once date is changed */
  onDateChanged: (startDate?: string, endDate?: string, isCalendarOpen?: boolean) => void;
  /** For partial checkout, keep a reference end date static to keep available selection range open */
  fixedReferenceEndDate?: string;
  /** String to edit default IconTextButtons */
  buttonClassName?: string;
  /** List of holidays */
  holidays?: string[];
};

function DateRow(props: Props) {
  const isSP = useDeviceType() === MOBILE_SCREEN;
  const [activeButton, setActiveButton] = useState<DateButtonType | undefined>(undefined);

  const {
    startDate,
    endDate,
    onDateChanged,
    fixedReferenceEndDate,
    startDateTitle = 'チェックイン',
    endDateTitle = 'チェックアウト',
    holidays,
  } = props;

  const {
    isOpen: isCalendarOpen,
    onOpen: onOpenCalendar,
    onClose: onCloseCalendar,
  } = useDialogHandler(false);

  const anchorEl = useRef<HTMLDivElement>(null);

  const onActiveButtonChanged = useCallback((button: string | undefined) => {
    setActiveButton(button as DateButtonType | undefined);
  }, []);

  const handleCloseCalendar = useCallback(() => {
    onActiveButtonChanged(undefined);
    onCloseCalendar();
  }, [onActiveButtonChanged, onCloseCalendar]);

  const onClickDateButton = useCallback(
    (selectedButton: DateButtonType) => {
      if (activeButton === selectedButton && isCalendarOpen) {
        handleCloseCalendar();
      } else if (!isCalendarOpen) {
        onOpenCalendar();
      }
      onActiveButtonChanged(selectedButton);
    },
    [activeButton, isCalendarOpen, onActiveButtonChanged, handleCloseCalendar, onOpenCalendar],
  );

  const onClickStartDateButton = useCallback(() => {
    onClickDateButton('startDate');
  }, [onClickDateButton]);

  const onClickEndDateButton = useCallback(() => {
    onClickDateButton('endDate');
  }, [onClickDateButton]);

  const onClickEmptyButton = useCallback(() => {
    onActiveButtonChanged('startDate');
    onOpenCalendar();
  }, [onActiveButtonChanged, onOpenCalendar]);

  const hasNoDate = !startDate && !endDate;

  const dateComponent = (
    <DatePicker
      className={cx(!isSP && styles.calendarPopupWrapper)}
      anchorEl={anchorEl.current}
      onDateChanged={onDateChanged}
      startDate={startDate}
      endDate={endDate}
      startDateLabel={startDateTitle}
      endDateLabel={endDateTitle}
      isCalendarOpen={isSP || isCalendarOpen}
      onOpenCalendar={onOpenCalendar}
      onCloseCalendar={handleCloseCalendar}
      fixedReferenceEndDate={fixedReferenceEndDate}
      onActiveButtonChanged={onActiveButtonChanged}
      selectedButton={activeButton}
      holidays={holidays}
    />
  );

  return (
    <>
      {!isSP ? (
        <div
          className={cx(styles.row, props.className)}
          ref={anchorEl}
          data-testid={`dateRow-wrapper`}
        >
          {hasNoDate && !isCalendarOpen ? (
            <IconTextButton
              className={cx(styles.rowElement, styles.noDateElement)}
              text={labels.noDates}
              textClassName={styles.fixedTextHeight}
              icon={<Calendar size={20} color={'default'} />}
              data-testid={`dateRow-startDate-button`}
              onClick={onClickEmptyButton}
            />
          ) : (
            <>
              <IconTextButton
                className={cx(
                  styles.rowElement,
                  styles.rowFrom,
                  props.buttonClassName,
                  isCalendarOpen && activeButton === 'startDate' && styles.calendarOpen,
                )}
                contentContainerClassName={styles.rowElementContentContainer}
                title={<span className={styles.titleText}>{startDateTitle}</span>}
                text={
                  startDate ? (
                    <L10nDate value={startDate} format={MONTH_DAY_YOUBI_WITH_SPACE_FORMAT} />
                  ) : (
                    ''
                  )
                }
                textClassName={styles.fixedTextHeight}
                icon={<Calendar size={20} color={'default'} />}
                data-testid={`dateRow-startDate-button`}
                onClick={onClickStartDateButton}
              />
              <div className={styles.arrowRightContainer}>
                <ArrowRightLine className={styles.arrowRight} size={17} />
              </div>
              <IconTextButton
                className={cx(
                  styles.rowElement,
                  styles.rowTo,
                  props.buttonClassName,
                  isCalendarOpen && activeButton === 'endDate' && styles.calendarOpen,
                )}
                contentContainerClassName={styles.rowElementContentContainer}
                title={<span className={styles.titleText}>{endDateTitle}</span>}
                text={
                  endDate ? (
                    <L10nDate value={endDate} format={MONTH_DAY_YOUBI_WITH_SPACE_FORMAT} />
                  ) : (
                    ''
                  )
                }
                textClassName={styles.fixedTextHeight}
                data-testid={`dateRow-endDate-button`}
                onClick={onClickEndDateButton}
              />
            </>
          )}
          {dateComponent}
        </div>
      ) : (
        <>
          <div className={styles.buttonRow}>
            <DateButton
              type="start"
              title="チェックイン"
              className={styles.buttonRowDateButton}
              date={startDate}
              onClick={onClickStartDateButton}
              isActive={activeButton === 'startDate'}
            />
            <div className={styles.buttonRowSeparator}>〜</div>
            <DateButton
              type="start"
              title="チェックアウト"
              className={styles.buttonRowDateButton}
              date={endDate}
              onClick={onClickEndDateButton}
              isActive={activeButton === 'endDate'}
            />
          </div>
          {dateComponent}
        </>
      )}

      <HiddenInput value={dayjs(startDate).date() || ''} id={URL_KEYS.CHECK_IN_DAY} />
      <HiddenInput value={dayjs(startDate).month() + 1 || ''} id={URL_KEYS.CHECK_IN_MONTH} />
      <HiddenInput value={dayjs(startDate).year() || ''} id={URL_KEYS.CHECK_IN_YEAR} />
      <HiddenInput value={dayjs(endDate).date() || ''} id={URL_KEYS.CHECK_OUT_DAY} />
      <HiddenInput value={dayjs(endDate).month() + 1 || ''} id={URL_KEYS.CHECK_OUT_MONTH} />
      <HiddenInput value={dayjs(endDate).year() || ''} id={URL_KEYS.CHECK_OUT_YEAR} />
    </>
  );
}

export default DateRow;
